import React from 'react';

import { JobPostingModule } from '@/components/job-posting-module';
import { CompanyContactsModule } from '@/components/job-posting/components/company-contacts-module';
import { CompanyVideo } from '@/components/job-posting/components/company-video';
import { CreateAlertModule } from '@/components/job-posting/components/create-alert-module';
import { JobMetadata } from '@/components/job-posting/components/job-metadata';
import {
  JobPostingDescription,
  JobPostingDescriptionPropType,
} from '@/components/job-posting/components/job-posting-description';
import { SalaryModule } from '@/components/job-posting/components/salary-module';
import { SimilarJobsModule } from '@/components/job-posting/components/similar-jobs-module';
import { JobPostingModuleStatusProvider, useUpdateTrackingModuleList } from '@/components/job-posting/contexts';
import { NonNullCompanyInfo } from '@/domain/job-details/components/company-module/employer-actions/employer-types';
import { JobSummaryModule } from '@/domain/job-details/components/job-summary-module';
import {
  CompanyKununuData,
  JobLocation,
  JobPostingVisibleJobFragment,
  JobSummary,
  JobVideo,
} from '@/graphql/generated';
import { useBreakpoints } from '@/hooks';
import { ConnectedContact } from '@/types/contact';

import { useModulesConfig } from '../hooks';
import { CompanyModule } from './company-module';
import { EmployerModule } from './employer-module';
import { Header } from './header';
import { HRContact } from './hr-contact';

export const JobPostingModules: React.FC<{ job: JobPostingVisibleJobFragment }> = ({ job }) => {
  const { modules, showModule } = useModulesConfig(job);
  const updateTrackingModuleList = useUpdateTrackingModuleList();
  const { isMobile } = useBreakpoints();
  const showHRFullRow = showModule('HRContact') && !showModule('Company');
  const companyId = job.companyInfo.company?.id;

  return (
    <JobPostingModuleStatusProvider modules={modules} notifyVisibleModuleList={updateTrackingModuleList}>
      <JobMetadata job={job} />
      <Header job={job} />
      {showModule('Summary') ? (
        <JobSummaryModule
          jobId={job.id}
          language={job.language as 'de' | 'en' | undefined}
          summary={job.summary as JobSummary}
        />
      ) : null}
      <JobPostingDescription
        logo={job.banner?.url}
        description={job.description as JobPostingDescriptionPropType}
        moduleName="Description"
        // Don't remove the jobCode. It's needed to know if we need to load a
        // custom 3rd party tracking script.
        jobCode={job.jobCode || null}
      />
      {showModule('Salary') ? <SalaryModule moduleName="Salary" salary={job.salary} /> : null}
      {showModule('Company') ? (
        <CompanyModule
          location={job.location as JobLocation}
          companyInfo={job.companyInfo as NonNullCompanyInfo}
          contact={job.contact as ConnectedContact}
          industry={job.companyInfo.company?.industry?.localizationValue}
          companyId={companyId}
        />
      ) : null}
      {showHRFullRow ? (
        <JobPostingModule>
          <HRContact
            contact={job.contact as ConnectedContact}
            alignment={isMobile ? 'vertical' : 'horizontal'}
            showTitle
            companyId={companyId}
          />
        </JobPostingModule>
      ) : null}
      {showModule('Culture') ? (
        <EmployerModule
          companyName={job.companyInfo.companyNameOverride}
          kununuData={job.companyInfo.company?.kununuData as CompanyKununuData}
          isCollapsingEnabled={true}
        />
      ) : null}
      {showModule('Contacts') ? (
        <CompanyContactsModule
          id={job.id}
          companyName={job.companyInfo?.company?.name}
          companyId={companyId}
          moduleName="Contacts"
        />
      ) : null}
      <CreateAlertModule moduleName="SearchAlert" />
      {showModule('Video') ? <CompanyVideo moduleName="Video" video={job.video as JobVideo} /> : null}
      <SimilarJobsModule moduleName="SimilarJobs" />
    </JobPostingModuleStatusProvider>
  );
};
